/**
 * Spring declarations — ADR-038 (Proposed): **what a spring chain IS, in the
 * file's own vocabulary, independent of any engine.**
 *
 * ONE responsibility: the shape of a declared spring chain, the tail prior's
 * defaults for one, and the lossless mapping to and from the standard glTF
 * carrier, `VRMC_springBone` 1.0. The kernel does not simulate; the adapter
 * runs the reference solver (@pixiv/three-vrm-springbone) over what this
 * declares, and World's runtime is asked to read the same extension.
 *
 * WHY VRMC_springBone, AND ITS SEMANTICS. The extension is the only
 * standardised skeletal-spring carrier for glTF, it is keyed by node INDEX
 * (so it survives any renaming a reader does), and every VRM reader —
 * three-vrm, UniVRM, World's validator — already recognises it. Using its
 * SHAPE while meaning some other solver's numbers would be a lie to every one
 * of those readers, so the numbers mean what the spec means: per joint,
 * `stiffness` pulls the tail back along the joint's initial bone axis,
 * `gravityPower` along `gravityDir`, both per second of simulation, `dragForce`
 * in [0, 1] removes that share of the Verlet velocity per step, and
 * `hitRadius` is the tail's collision sphere. In a spring, joint k is the bone
 * and joint k+1 its tail; the last joint's parameters are ignored by the spec.
 *
 * NAMES, NOT INDICES, IN HERE. The declaration speaks the bone names the FILE
 * speaks (`Tail3`, not the session's `Tail3_graft`); indices exist only at the
 * write/read boundary (`toVrmcSpringBone` / `fromVrmcSpringBone`), which the
 * adapter's export and load doors call with their own node tables.
 *
 * PROVENANCE rides in the extension's own `extras.poqpoq` (glTF permits
 * `extras` on every property): who decided, from which prior, with which
 * knobs — ADR-030 §14's posture. A reader that ignores it sees plain VRM.
 *
 * Depends on: zod. Depended on by: adapter-three springs (runtime, export,
 * load), the app's Body-tab spring control.
 */
import { z } from 'zod';
import { springDriveSchema } from './spring-drive.js';

/** The extension name (VRM 1.0, `VRMC_springBone`), spelled once. */
export const VRMC_SPRING_BONE = 'VRMC_springBone';

const finite = z.number().finite();
const vec3 = z.tuple([finite, finite, finite]);

export const springJointSchema = z.object({
  /** The joint's node, by the name the file speaks. */
  bone: z.string().min(1),
  hitRadius: finite.nonnegative(),
  stiffness: finite.nonnegative(),
  gravityPower: finite.nonnegative(),
  /** Unit vector, world (or center) space. */
  gravityDir: vec3,
  dragForce: finite.min(0).max(1),
});
export type SpringJoint = z.infer<typeof springJointSchema>;

export const springColliderShapeSchema = z.union([
  z.object({ sphere: z.object({ offset: vec3, radius: finite.nonnegative() }) }),
  z.object({ capsule: z.object({ offset: vec3, radius: finite.nonnegative(), tail: vec3 }) }),
]);
export type SpringColliderShape = z.infer<typeof springColliderShapeSchema>;

export const springColliderSchema = z.object({
  /** The node the collider rides; offsets are in that node's LOCAL frame (the spec). */
  bone: z.string().min(1),
  shape: springColliderShapeSchema,
});
export type SpringCollider = z.infer<typeof springColliderSchema>;

/** The knobs a person moves — the Body tab's three sliders — recorded as provenance. */
export const springKnobsSchema = z.object({
  stiffness: finite.nonnegative(),
  drag: finite.min(0).max(1),
  gravity: finite.nonnegative(),
});
export type SpringKnobs = z.infer<typeof springKnobsSchema>;

export const springDeclarationSchema = z.object({
  /** The spring's name in the file (`tail`). */
  name: z.string().min(1),
  /** Root → tip. At least two: a spring needs a bone and its tail. */
  joints: z.array(springJointSchema).min(2),
  colliders: z.array(springColliderSchema),
  /** Optional evaluation space (the spec's `center`); absent = world. */
  center: z.string().min(1).optional(),
  /** Who decided and from what — `extras.poqpoq.spring` on the spring. */
  provenance: z.object({
    /** e.g. `tail-prior@1` — the rule that made the numbers. */
    rule: z.string().min(1),
    knobs: springKnobsSchema,
    /** `human:<id>` once a person moved a knob; absent = the prior's defaults. */
    confirmedBy: z.string().regex(/^human:\S+$/).optional(),
    /**
     * The stability bound's inputs (ADR-038 §13.6): each sprung joint's segment
     * length (world units, bone → child at bind), so a retune re-applies the
     * bound. Absent = no bound (a spring this suite did not derive).
     */
    segmentLengths: z.array(finite.positive()).optional(),
  }),
  /**
   * ADR-038 R5 (ruled 2026-09-24): the layered drive — the spring's target is
   * the ANIMATED pose, a per-joint muscle weight holds the base to it, a mood
   * drives it (`spring-drive.ts`). Absent = the plain standard spring against
   * the bind. Written to `extras.poqpoq.drive`.
   */
  drive: springDriveSchema.optional(),
}).superRefine((d, ctx) => {
  if (d.drive === undefined) return;
  for (const key of ['gains', 'halfLivesS'] as const) {
    if (d.drive[key].length !== d.joints.length - 1) {
      ctx.addIssue({ code: 'custom', path: ['drive', key], message: `a drive declares ${key} per sprung joint (${d.joints.length - 1}); got ${d.drive[key].length}` });
    }
  }
  if (d.drive.hinge !== undefined && (d.drive.hinge.axes.length !== d.joints.length - 1 || d.drive.hinge.aspect.length !== d.joints.length - 1)) {
    ctx.addIssue({ code: 'custom', path: ['drive', 'hinge'], message: `a hinge declares an axis and an aspect per sprung joint (${d.joints.length - 1})` });
  }
  if (d.drive.axes.length !== d.joints.length) {
    ctx.addIssue({ code: 'custom', path: ['drive', 'axes'], message: `a drive declares axes per chain bone (${d.joints.length}); got ${d.drive.axes.length}` });
  }
});
export type SpringDeclaration = z.infer<typeof springDeclarationSchema>;

export class SpringDeclarationRefusal extends Error {
  override readonly name = 'SpringDeclarationRefusal';
}

// ---------------------------------------------------------------------------
// The tail prior's defaults (ADR-038 §3.2)
// ---------------------------------------------------------------------------

/**
 * The knobs' defaults for a tail. Provenance: memory `tail-anatomy-priors`
 * (small isotropic bend per joint, actuation at the base, passive whip
 * distally) turned into VRM units and MEASURED on the kobold's 93 cm tail
 * (`tail-spring-probe.test.ts`, 2026-09-23): at rest the tip settles 12° below
 * the rigid tail (19 cm drop); under the Mixamo walk the tip swings 42 cm
 * peak-to-peak relative to the pelvis; after a 30° hip turn it is inside 1 cm
 * of rest in 1.4 s. Drag 0.4 is VRoid's own default. A starting point for
 * Allen's eye, not a measurement of any animal (ADR-038 R4).
 */
export const TAIL_SPRING_KNOBS: SpringKnobs = { stiffness: 1.2, drag: 0.4, gravity: 0.04 };

/**
 * THE STABILITY BOUND (ADR-038 §13.6). The reference update adds
 * `(stiffness + gravityPower) · dt` to a joint's tail POSITION, in metres,
 * whatever its segment's length: on a short segment a high stiffness pulls the
 * tail most of its own length in one 60 Hz step, overshoots, and the chain
 * zig-zags toward the tip (Bonnie Beaver, "stiffness cranked all the way up …
 * goes all fluttery at the tip" — measured: tip-joint step jitter 6° → 12°
 * from stiffness 1.2 to 4). So the prior DECLARES, for every joint but the root
 * (whose parent is the smoothly animated body; it has its own, looser bound),
 * a stiffness no larger than
 * `SPRING_MAX_PULL_PER_STEP · L · 60 − gravityPower` (the root joint under
 * `SPRING_MAX_PULL_PER_STEP_ROOT`). Because the bound lives
 * in the declared numbers, every reader — World, three-vrm, any VRM runtime —
 * plays the same stable spring with no new arithmetic. 0.33 sits just above
 * the kobold's worst non-root joint at the default knobs (0.322), and the
 * root's 0.7 above its root (0.666), so the kobold's defaults declare exactly
 * what they declared before.
 */
export const SPRING_MAX_PULL_PER_STEP = 0.33;
/** The step rate the bound is computed for (the fixed clock, ADR-038 §2.2). */
export const SPRING_BOUND_STEP_HZ = 60;

/**
 * The root joint's bound: its parent is the smoothly animated body, not a
 * swinging segment, so it tolerates more (measured steady to 0.67 of its
 * segment per step on the kobold's defaults) — but not a stiffness knob of 4
 * on a 3 cm segment (2.2 per step on Bonnie).
 */
export const SPRING_MAX_PULL_PER_STEP_ROOT = 0.7;

/** A joint's stiffness under the stability bound (the root joint, k = 0, under the root's). */
export function boundedStiffness(stiffness: number, gravityPower: number, k: number, segmentLength: number | undefined): number {
  if (segmentLength === undefined) return stiffness;
  const kappa = k === 0 ? SPRING_MAX_PULL_PER_STEP_ROOT : SPRING_MAX_PULL_PER_STEP;
  return Math.max(0, Math.min(stiffness, kappa * segmentLength * SPRING_BOUND_STEP_HZ - gravityPower));
}

/** How far the stiffness falls from root to tip, as a fraction of the knob (the whip). */
export const TAIL_STIFFNESS_TIP_FRACTION = 0.35;

export interface TailSpringInput {
  /** The chain's bones, root → tip, by the names the file speaks (`Tail1…TailN`). */
  readonly bones: readonly string[];
  /** Cross-section radius at each joint, world units, root → tip — count + 1 values (the last is the tip). */
  readonly radii: readonly number[];
  readonly colliders: readonly SpringCollider[];
  readonly knobs?: SpringKnobs;
  readonly confirmedBy?: string;
  /** Each sprung joint's segment length (bone → child at bind, world units; count − 1 values) — the stability bound's input. */
  readonly segmentLengths?: readonly number[];
}

/**
 * A tail's spring from the prior: stiffness falling linearly from the knob
 * at the root to {@link TAIL_STIFFNESS_TIP_FRACTION} of it at the tip (the
 * base is actuated and thick, the tip whips), drag and gravity the knobs',
 * gravity straight down, and each joint's hit radius the flesh radius at its
 * TAIL (joint k collides at joint k+1 — the spec's tail).
 */
export function tailSpringDeclaration(input: TailSpringInput): SpringDeclaration {
  const n = input.bones.length;
  if (n < 2) throw new SpringDeclarationRefusal(`a tail spring needs two bones or more; this chain has ${n}`);
  if (input.radii.length !== n + 1) {
    throw new SpringDeclarationRefusal(`a tail spring needs a radius per joint and the tip (${n + 1}); got ${input.radii.length}`);
  }
  const knobs = input.knobs ?? TAIL_SPRING_KNOBS;
  const joints: SpringJoint[] = input.bones.map((bone, k) => {
    const t = n === 1 ? 0 : k / (n - 1);
    return {
      bone,
      hitRadius: input.radii[k + 1]!,
      stiffness: boundedStiffness(knobs.stiffness * (1 - (1 - TAIL_STIFFNESS_TIP_FRACTION) * t), knobs.gravity, k, input.segmentLengths?.[k]),
      gravityPower: knobs.gravity,
      gravityDir: [0, -1, 0],
      dragForce: knobs.drag,
    };
  });
  return springDeclarationSchema.parse({
    name: 'tail',
    joints,
    colliders: [...input.colliders],
    provenance: {
      rule: 'tail-prior@1',
      knobs,
      ...(input.confirmedBy !== undefined ? { confirmedBy: input.confirmedBy } : {}),
      ...(input.segmentLengths !== undefined ? { segmentLengths: [...input.segmentLengths] } : {}),
    },
  });
}

/** The same chain with new knobs: the prior re-applied, colliders and radii kept. */
export function retuneTailSpring(decl: SpringDeclaration, knobs: SpringKnobs, confirmedBy?: string): SpringDeclaration {
  const n = decl.joints.length;
  return springDeclarationSchema.parse({
    ...decl,
    joints: decl.joints.map((j, k) => ({
      ...j,
      stiffness: boundedStiffness(
        knobs.stiffness * (1 - (1 - TAIL_STIFFNESS_TIP_FRACTION) * (n === 1 ? 0 : k / (n - 1))),
        knobs.gravity,
        k,
        decl.provenance.segmentLengths?.[k],
      ),
      gravityPower: knobs.gravity,
      dragForce: knobs.drag,
    })),
    provenance: { ...decl.provenance, knobs, ...(confirmedBy !== undefined ? { confirmedBy } : {}) },
  });
}

// ---------------------------------------------------------------------------
// The carrier: VRMC_springBone 1.0 (write / read)
// ---------------------------------------------------------------------------

/** The subset of the VRMC_springBone 1.0 JSON this suite writes and reads. */
export interface VrmcSpringBoneJson {
  specVersion: '1.0';
  colliders?: { node: number; shape: SpringColliderShape }[];
  colliderGroups?: { name?: string; colliders: number[] }[];
  springs?: {
    name?: string;
    joints: { node: number; hitRadius: number; stiffness: number; gravityPower: number; gravityDir: [number, number, number]; dragForce: number }[];
    colliderGroups?: number[];
    center?: number;
    extras?: Record<string, unknown>;
  }[];
}

/**
 * Declarations → the extension JSON. `nodeIndexOf` resolves a file name to
 * its node index at the write; a name it cannot resolve refuses by name (a
 * spring over a bone the file does not carry is a broken file, never a
 * silently shorter chain). One collider group per spring, named after it.
 */
export function toVrmcSpringBone(decls: readonly SpringDeclaration[], nodeIndexOf: (bone: string) => number | undefined): VrmcSpringBoneJson {
  const missing: string[] = [];
  const idx = (bone: string): number => {
    const i = nodeIndexOf(bone);
    if (i === undefined) missing.push(bone);
    return i ?? -1;
  };
  const colliders: NonNullable<VrmcSpringBoneJson['colliders']> = [];
  const colliderGroups: NonNullable<VrmcSpringBoneJson['colliderGroups']> = [];
  const springs: NonNullable<VrmcSpringBoneJson['springs']> = [];
  for (const d of decls) {
    const group: number[] = [];
    for (const c of d.colliders) {
      group.push(colliders.length);
      colliders.push({ node: idx(c.bone), shape: c.shape });
    }
    const groupIndex = group.length > 0 ? colliderGroups.push({ name: d.name, colliders: group }) - 1 : undefined;
    springs.push({
      name: d.name,
      joints: d.joints.map((j) => ({
        node: idx(j.bone),
        hitRadius: j.hitRadius,
        stiffness: j.stiffness,
        gravityPower: j.gravityPower,
        gravityDir: [...j.gravityDir],
        dragForce: j.dragForce,
      })),
      ...(groupIndex !== undefined ? { colliderGroups: [groupIndex] } : {}),
      ...(d.center !== undefined ? { center: idx(d.center) } : {}),
      extras: { poqpoq: { spring: d.provenance, ...(d.drive !== undefined ? { drive: d.drive } : {}) } },
    });
  }
  if (missing.length > 0) {
    throw new SpringDeclarationRefusal(`the spring names bones the file does not carry: ${[...new Set(missing)].join(', ')}`);
  }
  return {
    specVersion: '1.0',
    ...(colliders.length > 0 ? { colliders, colliderGroups } : {}),
    springs,
  };
}

/**
 * The extension JSON → declarations, by the file's node names. A spring this
 * suite did not write (no `extras.poqpoq.spring`) is ADOPTED with provenance
 * `imported` — never re-derived, never duplicated. Refuses by name a spring
 * whose joints or colliders point at nodes the file does not have.
 */
export function fromVrmcSpringBone(ext: unknown, nodeNameOf: (index: number) => string | undefined): SpringDeclaration[] {
  if (ext === null || typeof ext !== 'object') throw new SpringDeclarationRefusal('VRMC_springBone is not an object');
  const json = ext as Partial<VrmcSpringBoneJson> & { specVersion?: unknown };
  if (json.specVersion !== '1.0' && json.specVersion !== '1.0-beta') {
    throw new SpringDeclarationRefusal(`VRMC_springBone specVersion ${JSON.stringify(json.specVersion)} is not one this reader knows (1.0)`);
  }
  const name = (i: number, what: string): string => {
    const n = nodeNameOf(i);
    if (n === undefined || n === '') throw new SpringDeclarationRefusal(`VRMC_springBone ${what} points at node ${i}, which has no name in this file`);
    return n;
  };
  const colliders = (json.colliders ?? []).map((c, i) => ({ bone: name(c.node, `collider #${i}`), shape: c.shape }));
  const groups = json.colliderGroups ?? [];
  return (json.springs ?? []).map((s, iSpring) => {
    const extras = s.extras as { poqpoq?: { spring?: unknown; drive?: unknown } } | undefined;
    const knobs = { stiffness: s.joints[0]?.stiffness ?? 0, drag: s.joints[0]?.dragForce ?? 0, gravity: s.joints[0]?.gravityPower ?? 0 };
    const provenance = extras?.poqpoq?.spring ?? { rule: 'imported', knobs };
    return springDeclarationSchema.parse({
      name: s.name ?? `spring${iSpring}`,
      joints: s.joints.map((j, k) => ({
        bone: name(j.node, `spring "${s.name ?? iSpring}" joint #${k}`),
        hitRadius: j.hitRadius ?? 0,
        stiffness: j.stiffness ?? 1,
        gravityPower: j.gravityPower ?? 0,
        gravityDir: j.gravityDir ?? [0, -1, 0],
        dragForce: j.dragForce ?? 0.5,
      })),
      colliders: (s.colliderGroups ?? []).flatMap((g) => (groups[g]?.colliders ?? []).map((ci) => {
        const c = colliders[ci];
        if (c === undefined) throw new SpringDeclarationRefusal(`VRMC_springBone collider group ${g} names collider ${ci}, which the file does not have`);
        return c;
      })),
      ...(s.center !== undefined ? { center: name(s.center, `spring "${s.name ?? iSpring}" center`) } : {}),
      provenance,
      ...(extras?.poqpoq?.drive !== undefined ? { drive: extras.poqpoq.drive } : {}),
    });
  });
}
